import React, { useEffect, useState } from 'react';
import { useRemote, useStorage } from '../../Utils/Utils';
import { useWebsockets } from "../../Utils/WebsocketReceiverHook";
import FlexWrapper from '../../layouts/FlexWrapper';
import Button from '../Buttons/Button';
import TextArea from '../Input/TextArea';
import Comment from './Comment';
import Checkbox from '../Checkbox';
import './CommentsSection.scss';
import { CommentFlag } from './CommentConstants';
import Tooltip from '../popups/Tooltip';

const CommentsSection = ({ ticker, templateName, workflowStatusId, requestId, updateParentsComments = () => null, setShowResolveComment, setText, rejectionSubmitted, refreshRequestsList, uneditable, showTitle }) => {

	const [commentInputValue, setCommentInputValue] = useState("")
	const [comments, setComments] = useState([])
	const [authors, setAuthors] = useState([])
	const [me, setMe] = useState({})
	const [requiresResolution, setRequiresResolution] = useState(false)
	const [requiresKeep, setRequiresKeeps] = useState(false)

	const remote = useRemote()
	const storage = useStorage()
	const sockets = useWebsockets()

	const perms = me?.perms || []
	const userRole = perms.includes("COMMENTS_PEER") ? "Peer" : perms.includes("COMMENTS_COMPLIANCE") ? "Compliance" : perms.includes("COMMENTS_SUPERVISOR") ? "Supervisor" : null

	useEffect(() => {
		storage.getOrFetch('/crm/users').then((val) => {
			setAuthors(val);
		});
		storage.getOrFetch('/crm/me').then(setMe)

	}, [])

	useEffect(() => {
		// fetch comments
		if (!ticker) return;
		if (!templateName) return;
		refresh()
		const identifier = btoa(ticker);
		const encodedTemplateName = encodeURIComponent(templateName);
		const url = `/comments/${identifier}/${encodedTemplateName}${requestId && workflowStatusId ? '?requestId=' + requestId + "&workflowStatusId=" + workflowStatusId : requestId ? '?requestId=' + requestId : workflowStatusId ? '?workflowStatusId=' + workflowStatusId : ""}`
		const socketsPath = `/api/comments/${identifier}/${encodedTemplateName}${requestId && workflowStatusId ? '?requestId=' + requestId + "&workflowStatusId=" + workflowStatusId : requestId ? '?requestId=' + requestId : workflowStatusId ? '?workflowStatusId=' + workflowStatusId : ""}`

		sockets.subscribe({
			[socketsPath]: [(message) => {
				storage.refresh(url).then((val) => {
					const fetchedComments = []
					val.forEach(comment => {
						if (!comment.replyTo) {
							fetchedComments.push(comment)
						} else {
							const parentComment = val.find(c => c.commentId === comment.replyTo)
							if (parentComment && !parentComment.replies) {
								parentComment.replies = [comment]
							} else {
								parentComment.replies.push(comment)
							}
						}
					})
					setComments([])
					const sortedComments = sortComments(fetchedComments);
					setComments(sortedComments)
					updateParentsComments(sortedComments)
					refreshRequestsList && refreshRequestsList()
				})
			}]
		})
		return () => {
			sockets.unsubscribe([socketsPath])
		}

	}, [ticker, templateName, rejectionSubmitted])

	function refresh() {
		const identifier = btoa(ticker);
		let url = `/comments/${identifier}/${templateName}`
		const params = new URLSearchParams();
		if (requestId) params.append("requestId", requestId);
		if (workflowStatusId) params.append("workflowStatusId", workflowStatusId);

		if (params.toString()) {
			url += `?${params.toString()}`;
		}
		remote.get(url).then((val) => {
			const fetchedComments = []
			val.forEach(comment => {
				if (!comment.replyTo) {
					fetchedComments.push(comment)
				} else {
					const parentComment = val.find(c => c.commentId === comment.replyTo)
					if (parentComment && !parentComment.replies) {
						parentComment.replies = [comment]
					} else {
						parentComment.replies.push(comment)
					}
				}
			})

			setComments([])
			const sortedComments = sortComments(fetchedComments);
			setComments(sortedComments)
			updateParentsComments(sortedComments)
		})
	}

	const sortComments = (commentsList) => {
		return commentsList.sort((a, b) => {
			// Priority 1: Persisted comments first (regardless of pin status)
			if (a.isPersisted && !b.isPersisted) return -1;
			if (!a.isPersisted && b.isPersisted) return 1;

			// If both have same persist status, sort by timestamp (newest first)
			return b.commentedOn.localeCompare(a.commentedOn);
		});
	}

	const handleCommentSubmit = (commentId, replyInputValue, callback) => {
		const DTO = {
			replyTo: commentId,
			authorRole: userRole,
			comment: commentId ? replyInputValue : commentInputValue,
			flag: requiresResolution ? CommentFlag.RAISED_COMMENT : null,
			isPersisted: requiresKeep ? true : false
		}

		const identifier = btoa(ticker);

		let url = `/comments/${identifier}/${templateName}`
		const params = new URLSearchParams();
		if (requestId) params.append("requestId", requestId);
		if (workflowStatusId) params.append("workflowStatusId", workflowStatusId);

		if (params.toString()) {
			url += `?${params.toString()}`;
		}
		remote.post(url, DTO)
			.then((response) => {
				if (commentId) {
					let newComments = comments.map(comment => {
						if (comment.commentId === commentId) {
							if (!comment.replies) comment.replies = [response.data]
							else comment.replies.push(response.data)
						}
						return comment
					})
					setComments(newComments)
					updateParentsComments(newComments)
					setRequiresResolution(false)
					setRequiresKeeps(false)
					callback && callback()
				} else {
					const old = [...comments]
					const newComments = [response.data, ...old]
					const sortedNewComments = sortComments(newComments)
					setComments([])
					updateParentsComments([])
					setComments(sortedNewComments)
					updateParentsComments(sortedNewComments)
					setCommentInputValue("")
					setRequiresResolution(false)
					setRequiresKeeps(false)
				}
			}
			)
	}


	return (
		<>
			{showTitle && <p>Comments</p>}
			{!uneditable &&
				<FlexWrapper direction="column" className="comments-input-container" align="start">
					<TextArea
						placeholder="Add a comment"
						className="add-comment-text-area"
						value={commentInputValue}
						onChange={(e) => setCommentInputValue(e.target.value)}
					/>
					<FlexWrapper gap='s'>
						<Checkbox
							className="no-margin cursor-default"
							checked={requiresResolution}
							label="Require resolution"
							onChange={(e) => setRequiresResolution(!requiresResolution)} />
					</FlexWrapper>
					<FlexWrapper gap='s'>
						<Tooltip label="Locking will make this comment visible across all workflows for this document">
						<Checkbox
							className="no-margin cursor-default"
							checked={requiresKeep}
							label="Keep for future publications"
							onChange={(e) => setRequiresKeeps(!requiresKeep)} />
						</Tooltip>
					</FlexWrapper>
					<Button size="small" disabled={!userRole || !commentInputValue} fullWidth onClick={() => handleCommentSubmit()} >Submit Comment</Button>
				</FlexWrapper >
			}
			<FlexWrapper direction="column" className="comments-outer-container" align="start">
				{comments && comments.length > 0 && comments.map((comment, idx) => (
					<Comment
						ticker={ticker}
						templateName={templateName}
						isReply
						comment={comment}
						key={comment.commentId + idx}
						idx={idx}
						isLastComment={(idx + 1) === comments.length}
						authors={authors}
						userRole={userRole}
						handleCommentSubmit={handleCommentSubmit}
						refresh={refresh}
						setShowResolveComment={setShowResolveComment}
						setText={setText}
						workflowStatusId={workflowStatusId}
						requestId={requestId}
						uneditable={uneditable}
					/>
				))}
			</FlexWrapper>
		</>
	);
}

export default CommentsSection;